import { Fragment, useCallback, useMemo, useState, type ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  ChevronRight,
  ClipboardList,
  Columns3,
  Compass,
  GitBranch,
  LineChart as LineChartIcon,
  UserRound,
  FileSpreadsheet,
  Filter,
  Gauge,
  Inbox,
  Layers,
  ListChecks,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Rows3,
  Search,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react'
import {
  GenericSortHeader,
  dealTableHeadRowClass,
  dealTableRowClass,
  useGenericTableSort,
} from '@/components/shared/DealTable'
import {
  BookUniverseLine,
  DASHBOARD_REFETCH_MS,
  DashboardGrid,
  DashboardShell,
  DashboardTabs,
  FigureValue,
  GridCell,
  KpiStrip,
  MatrixPanel,
  NO_VALUE,
  Panel,
  dashboardQueryKey,
  figureToneClass,
  formatFigure,
  panelErrorMessage,
  useDashboardView,
  useDrillDown,
} from '@/components/dashboards'
import type { DashboardTab } from '@/components/dashboards'
import { chartSemanticProgressColor } from '@/config/chartTheme'
import api from '@/services/api'
import { dashboardsService, downloadDrillExport } from '@/services/dashboards'
import type {
  BookUniverse,
  DashboardPanelData,
  DashboardPayload,
  DrillDescriptor,
  Figure,
  FigureFormat,
  MatrixPanelData,
} from '@/types/dashboards'
import { queueKicker, rosterOptionLabel } from './uwQueueCopy'
import FlowChart from '@/components/analytics/explorer/FlowChart'
import { BasisNote } from '@/components/dashboards/KnownFigures'
import FunnelStrip from '@/components/analytics/explorer/FunnelStrip'
import PipelineExplorer from '@/components/analytics/explorer/PipelineExplorer'
import Leaderboard from '@/components/analytics/people/Leaderboard'
import TeamWorkloadPanel from '@/components/analytics/people/TeamWorkload'
import type {
  FlowResponse,
  FunnelResponse,
  PivotRow,
  WorkloadResponse,
} from '@/services/dashboardAnalytics'

// ── Wire shapes ────────────────────────────────────────────────────────────
// `types/dashboards.ts` declares the kit's generic panel shapes; the
// underwriting service ships panel-specific documents (a stage row carries two
// Figures, a queue row carries a record). Those shapes are declared here rather
// than widened in the shared types, because only this board reads them.

interface ScopePanel {
  analystEmail: string | null
  /** True when the board is showing the assignments no analyst owns. */
  unassigned?: boolean
  /** True when the board is showing every queue at once — asked for, or
   *  because the signed-in reader holds no assignments of their own. */
  desk?: boolean
  assignments: number
}

/** What `?analyst=` reads as "every queue". */
const DESK_SCOPE = '__desk__'

/** The page's own URL value for "my queue" — the request then carries no
 *  `analyst`, and the server answers with the reader's queue (or the desk
 *  when they hold none). */
const MY_QUEUE = 'me'

/** Book-depth panels the board ships beside the queue (CONTRACT_ADDENDUM). */
interface LeaderboardSlice {
  dimension: string
  label: string
  rows: PivotRow[]
  notRecorded?: { count: number; dealIds: string[] }
}

interface LeaderboardsPanel {
  underwriter?: LeaderboardSlice
  brokerage?: LeaderboardSlice
  basis?: string
}


interface StageRow {
  stage: string
  label: string
  count: Figure
  avgDwellDays: Figure
}

interface QueueRow {
  id: string
  deal: string | null
  loanId: string | null
  amount: number | null
  analystEmail: string | null
  stage: string
  status: string
  modelStatus: string | null
  dataReadyPct: number | null
  openExceptions: number | null
  dueAt: string | null
  daysToDue: number | null
  dwellDays: number | null
  href: string | null
  drill: DrillDescriptor | null
}

interface QuestionRow {
  id: string
  question: string
  directedTo: string
  askedBy: string | null
  askedAt: string | null
  ageDays: number | null
}

interface DataRequirementRow {
  requirement: string
  label: string
  completeness: Figure
  outstanding: Figure
}

interface SensitivityRow {
  scenario: string
  label: string
  runs: number
  deltaDscr: number | null
  resultingDscr: number | null
  resultingLtv: number | null
}

interface AttentionRow {
  source: string
  title: string
  detail: string | null
  amount: number | null
  daysToDeadline: number | null
  severity: string | null
  action: string
  score: number
  drill: DrillDescriptor | null
  href: string | null
}

interface RosterRow {
  analystEmail: string | null
  /** What to send back as `?analyst=` to open this row.
   *
   *  A queue nobody owns cannot be addressed by its email — there is none,
   *  and an empty string is what the route reads as "no scope given", which
   *  resolves to the caller's own queue. The server names the sentinel and
   *  the page sends it back verbatim. Optional: a board built before the
   *  sentinel landed ships only the email. */
  scopeValue?: string
  /** The person's label: every staff member is listed, at 0 when they hold
   *  no live work (T-54). Null for the Unassigned queue; absent on a board
   *  built before it shipped. */
  name?: string | null
  active: number
  overdue: number
  drill: DrillDescriptor | null
}

/** Kit panels are typed as the union; these arrive under the same keys but with
 *  the board's own shapes, so the cast is deliberate and local. */
function readPanel<T>(panels: Record<string, DashboardPanelData>, key: string): T | null {
  const value = panels[key] as unknown
  return value ? (value as T) : null
}

// ── Board query ────────────────────────────────────────────────────────────

/**
 * Same endpoint the kit's `useDashboard` calls, plus the `analyst` scope the
 * board supports. A manager reviewing another analyst's queue is a first-class
 * case on this board (spec §1 assumption 3), and the shared hook takes no
 * parameters — so the scope lives in the query key here rather than being lost.
 */
/** Who the board is showing, as the reader picked it — or as the server
 *  chose it, when the reader holds nothing and would otherwise see an empty
 *  queue standing in for the whole desk. */
function scopeLabel(scope: ScopePanel, picked: string | null): string {
  if (scope.unassigned) return 'Unassigned'
  if (scope.desk) return picked === DESK_SCOPE ? 'Whole desk (manager default)' : 'Whole desk (your queue is empty)'
  return scope.analystEmail ?? 'Signed-in analyst'
}

function useUnderwritingBoard(analystEmail: string | null) {
  const query = useQuery<DashboardPayload>({
    queryKey: [...dashboardQueryKey('underwriting'), analystEmail ?? 'self'],
    queryFn: ({ signal }) =>
      api
        .get<DashboardPayload>('/dashboards/underwriting', {
          params: analystEmail ? { analyst: analystEmail } : undefined,
          signal,
        })
        .then((response) => response.data),
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: true,
    // Switching analyst must not blank the board back to skeletons — the
    // previous queue stays readable until the new one lands.
    placeholderData: keepPreviousData,
  })

  return {
    panels: query.data?.panels ?? {},
    partition: query.data?.partition ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  }
}

// ── Display helpers ────────────────────────────────────────────────────────

/** A display-only Figure. Carries no drill, so it renders as plain text — a
 *  number the API did not ship a descriptor for must not advertise a click. */
function fig(label: string, value: number | string | null, format: FigureFormat): Figure {
  return { label, value, format }
}

function figureNumber(figure: Figure): number | null {
  return typeof figure.value === 'number' ? figure.value : null
}

/** "12d", or "--" — never "--d", which reads as a measured zero-ish duration. */
function daysText(value: number | null | undefined): string {
  const text = formatFigure(fig('Days', value ?? null, 'count'))
  return text === NO_VALUE ? text : `${text}d`
}

function dueLabel(days: number | null | undefined): string {
  if (days == null) return '--'
  const whole = Math.round(days)
  if (whole < 0) return `${Math.abs(whole)}d late`
  if (whole === 0) return 'today'
  return `${whole}d`
}

function dueTone(days: number | null | undefined): string {
  if (days == null) return 'text-stone-600'
  if (days < 0) return 'text-red-400'
  if (days <= 2) return 'text-amber-300'
  return 'text-stone-300'
}

function shortStamp(value: string | null): string {
  if (!value) return '--'
  const at = Date.parse(value)
  if (!Number.isFinite(at)) return '--'
  return new Date(at).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function titleCase(value: string | null | undefined): string {
  if (!value) return '--'
  return value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

const SEVERITY_DOT: Record<string, string> = {
  high: 'bg-red-400',
  medium: 'bg-amber-400',
  low: 'bg-stone-500',
}

// ── Queue filtering ────────────────────────────────────────────────────────

type QueueTab = 'all' | 'action' | 'due48' | 'awaiting' | 'review'

const QUEUE_TABS: Array<{ key: QueueTab; label: string; match: (row: QueueRow) => boolean }> = [
  { key: 'all', label: 'All assignments', match: () => true },
  {
    key: 'action',
    label: 'Needs action',
    match: (row) => (row.openExceptions ?? 0) > 0 || (row.daysToDue ?? Infinity) < 0,
  },
  { key: 'due48', label: 'Due 48 hours', match: (row) => (row.daysToDue ?? Infinity) <= 2 },
  {
    key: 'awaiting',
    label: 'Awaiting data',
    match: (row) => row.dataReadyPct != null && row.dataReadyPct < 100,
  },
  {
    key: 'review',
    label: 'In review',
    match: (row) => row.status === 'in_review' || row.stage === 'qc_review',
  },
]

/** Tokens the expression box understands. Listed once so the Filters legend and
 *  the parser cannot drift apart. */
const EXPRESSION_TOKENS: Array<{ token: string; describes: string }> = [
  { token: 'stage:qc_review', describes: 'underwriting stage contains the value' },
  { token: 'model:in_progress', describes: 'model status contains the value' },
  { token: 'status:in_review', describes: 'assignment status contains the value' },
  { token: 'due:<=2', describes: 'days to due date, with < <= > >= or =' },
  { token: 'ready:<75', describes: 'diligence docs received percent' },
  { token: 'exceptions:>0', describes: 'open exception count' },
]

const NUMERIC_FIELDS: Record<string, (row: QueueRow) => number | null> = {
  due: (row) => row.daysToDue,
  ready: (row) => row.dataReadyPct,
  exceptions: (row) => row.openExceptions,
  dwell: (row) => row.dwellDays,
}

const TEXT_FIELDS: Record<string, (row: QueueRow) => string> = {
  stage: (row) => row.stage,
  model: (row) => row.modelStatus ?? '',
  status: (row) => row.status,
  analyst: (row) => row.analystEmail ?? '',
  deal: (row) => `${row.deal ?? ''} ${row.loanId ?? ''}`,
}

function compare(left: number, operator: string, right: number): boolean {
  switch (operator) {
    case '<':
      return left < right
    case '<=':
      return left <= right
    case '>':
      return left > right
    case '>=':
      return left >= right
    default:
      return left === right
  }
}

/**
 * Applies one whitespace-separated token to a row.
 *
 * A bare word is free text over the deal and loan id. An unrecognised
 * `key:value` token matches nothing rather than being silently ignored — a
 * filter that quietly does not apply is how an operator reads the wrong queue.
 */
function matchesToken(row: QueueRow, token: string): boolean {
  const separator = token.indexOf(':')
  if (separator < 0) {
    return TEXT_FIELDS.deal(row).toLowerCase().includes(token.toLowerCase())
  }

  const field = token.slice(0, separator).toLowerCase()
  const rawValue = token.slice(separator + 1)
  if (!rawValue) return true

  const textAccessor = TEXT_FIELDS[field]
  if (textAccessor) return textAccessor(row).toLowerCase().includes(rawValue.toLowerCase())

  const numericAccessor = NUMERIC_FIELDS[field]
  if (numericAccessor) {
    const match = /^(<=|>=|<|>|=)?\s*(-?\d+(?:\.\d+)?)$/.exec(rawValue)
    if (!match) return false
    const actual = numericAccessor(row)
    // An absent measurement satisfies no numeric predicate — it is unknown,
    // not zero.
    if (actual == null) return false
    return compare(actual, match[1] ?? '=', Number(match[2]))
  }

  return false
}

// ── Row 2: the stage flow ──────────────────────────────────────────────────

function StageFlow({ rows, activeStage }: { rows: StageRow[]; activeStage: string | null }) {
  return (
    <div className="flex items-stretch gap-0.5">
      {rows.map((row, index) => (
        <Fragment key={row.stage}>
          {index > 0 && (
            <div className="flex flex-shrink-0 items-center text-stone-700" aria-hidden="true">
              <ChevronRight size={11} />
            </div>
          )}
          <div
            className={clsx(
              'surface-panel min-w-0 flex-1 rounded-md px-2 py-1.5 transition-colors',
              row.stage === activeStage && '!border-amber-400/35 bg-amber-400/[0.05]',
            )}
          >
            <div className="metric-cell-label break-words leading-tight" title={row.label}>
              {row.label}
            </div>
            <div className="mt-0.5 flex items-baseline justify-between gap-1">
              <span className={clsx('text-[15px] font-semibold', figureToneClass(row.count.tone))}>
                <FigureValue figure={row.count} />
              </span>
              <span
                className="font-mono text-[9px] text-stone-500"
                title="Average days in this stage"
              >
                {daysText(figureNumber(row.avgDwellDays))} avg
              </span>
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  )
}

// ── Row 4: the queue ───────────────────────────────────────────────────────

interface QueueColumn {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: string
  optional?: boolean
  sortValue: (row: QueueRow) => string | number
  render: (row: QueueRow) => ReactNode
}

function DataReadyBar({ pct }: { pct: number | null }) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="chart-viz-track h-1.5 w-14 rounded-full">
        {pct != null && (
          <div
            className="chart-viz-bar chart-viz-bar--rounded h-full"
            style={{
              width: `${Math.max(0, Math.min(100, pct))}%`,
              background: chartSemanticProgressColor(pct),
            }}
          />
        )}
      </div>
      <span
        className={clsx(
          'w-9 text-right font-mono text-[11px] tabular-nums',
          pct == null ? 'text-stone-600' : 'text-stone-200',
        )}
      >
        {formatFigure(fig('Diligence docs received', pct, 'pct'))}
      </span>
    </div>
  )
}

function ExceptionBadge({ count }: { count: number | null }) {
  if (count == null) return <span className="text-stone-600">--</span>
  return (
    <span
      className={clsx(
        'inline-flex min-w-[22px] justify-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
        count > 0
          ? 'border-red-500/30 bg-red-500/10 text-red-300'
          : 'border-emerald-500/25 bg-emerald-500/[0.07] text-emerald-300',
      )}
    >
      {count}
    </span>
  )
}

function DealCell({ row }: { row: QueueRow }) {
  const { open } = useDrillDown()
  const name = row.deal ?? row.loanId ?? '--'

  return (
    <div className="min-w-0">
      {row.drill ? (
        <button
          type="button"
          onClick={() => open(row.drill)}
          title={`${row.drill.title} — open records`}
          className="block max-w-full truncate text-left text-[12px] text-stone-100 underline decoration-amber-400/25 decoration-dotted underline-offset-[3px] transition-colors hover:text-amber-200 hover:decoration-amber-300/70 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
        >
          {name}
        </button>
      ) : (
        <span className="block truncate text-[12px] text-stone-100">{name}</span>
      )}
      <span className="block truncate font-mono text-[9px] text-stone-600">
        {row.loanId ?? '--'}
      </span>
    </div>
  )
}

function buildQueueColumns(): QueueColumn[] {
  return [
    {
      key: 'deal',
      label: 'Deal / property',
      width: 'w-[24%]',
      sortValue: (row) => (row.deal ?? row.loanId ?? '').toLowerCase(),
      render: (row) => <DealCell row={row} />,
    },
    {
      key: 'stage',
      label: 'Stage',
      width: 'w-[12%]',
      sortValue: (row) => row.stage,
      render: (row) => <span className="text-stone-300">{titleCase(row.stage)}</span>,
    },
    {
      key: 'amount',
      label: 'Requested $',
      align: 'right',
      width: 'w-[11%]',
      sortValue: (row) => row.amount ?? Number.NEGATIVE_INFINITY,
      render: (row) => (
        <span className="font-mono tabular-nums">
          {formatFigure(fig('Requested', row.amount, 'currencyM'))}
        </span>
      ),
    },
    {
      key: 'analyst',
      label: 'Analyst',
      width: 'w-[14%]',
      optional: true,
      sortValue: (row) => (row.analystEmail ?? '').toLowerCase(),
      render: (row) =>
        row.analystEmail ? (
          <Link
            to={`/dashboards/people?view=person&role=responsibleParty&name=${encodeURIComponent(row.analystEmail)}`}
            onClick={(event) => event.stopPropagation()}
            title={`Open ${row.analystEmail}'s profile`}
            className="block truncate text-stone-300 underline decoration-amber-400/20 underline-offset-[3px] hover:text-amber-200"
          >
            {row.analystEmail}
          </Link>
        ) : (
          <span className="text-stone-600">--</span>
        ),
    },
    {
      key: 'model',
      label: 'Model',
      width: 'w-[10%]',
      optional: true,
      sortValue: (row) => row.modelStatus ?? '',
      render: (row) => <span className="text-stone-400">{titleCase(row.modelStatus)}</span>,
    },
    {
      key: 'dataReady',
      // Diligence documents received against the checklist, not field
      // completeness (that is the pipeline's "Fields complete").
      label: 'Diligence docs',
      align: 'right',
      width: 'w-[13%]',
      sortValue: (row) => row.dataReadyPct ?? Number.NEGATIVE_INFINITY,
      render: (row) => <DataReadyBar pct={row.dataReadyPct} />,
    },
    {
      key: 'exceptions',
      label: 'Exceptions',
      align: 'center',
      width: 'w-[9%]',
      sortValue: (row) => row.openExceptions ?? Number.NEGATIVE_INFINITY,
      render: (row) => <ExceptionBadge count={row.openExceptions} />,
    },
    {
      key: 'dwell',
      label: 'In stage',
      align: 'right',
      width: 'w-[8%]',
      optional: true,
      sortValue: (row) => row.dwellDays ?? Number.NEGATIVE_INFINITY,
      render: (row) => (
        <span className="font-mono text-[11px] tabular-nums text-stone-400">
          {daysText(row.dwellDays)}
        </span>
      ),
    },
    {
      key: 'due',
      label: 'Due',
      align: 'right',
      width: 'w-[9%]',
      sortValue: (row) => row.daysToDue ?? Number.POSITIVE_INFINITY,
      render: (row) => (
        <span
          className={clsx('font-mono text-[11px] tabular-nums', dueTone(row.daysToDue))}
          title={row.dueAt ? shortStamp(row.dueAt) : undefined}
        >
          {dueLabel(row.daysToDue)}
        </span>
      ),
    },
  ]
}

interface QueueTableProps {
  rows: QueueRow[]
  columns: QueueColumn[]
  selectedId: string | null
  onSelect: (row: QueueRow) => void
  compact: boolean
}

function QueueTable({ rows, columns, selectedId, onSelect, compact }: QueueTableProps) {
  const navigate = useNavigate()

  const getValue = useCallback(
    (row: QueueRow, columnIndex: number) => columns[columnIndex].sortValue(row),
    [columns],
  )

  const { sortColumn, sortDir, toggleSort, sortedRows } = useGenericTableSort(rows, getValue, null)
  const hasWorkspace = rows.some((row) => row.href)

  return (
    <div
      className={clsx(
        'min-h-0 flex-1 overflow-auto scrollbar-thin',
        compact ? '[&_td]:!py-1' : '[&_td]:!py-2',
      )}
    >
      <table className="dense-table">
        <thead className="sticky top-0 z-10">
          <tr className={dealTableHeadRowClass()}>
            {columns.map((column, index) => (
              <GenericSortHeader
                key={column.key}
                label={column.label}
                columnIndex={index}
                sortColumn={sortColumn}
                sortDir={sortDir}
                onSort={toggleSort}
                align={column.align}
                className={column.width}
              />
            ))}
            {hasWorkspace && <th className="w-[34px]" aria-label="Open in workspace" />}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row)}
              className={dealTableRowClass(row.id === selectedId, 'cursor-pointer')}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
              {hasWorkspace && (
                <td className="text-right">
                  {row.href && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        navigate(row.href as string)
                      }}
                      title="Open in workspace"
                      aria-label="Open in workspace"
                      className="rounded p-0.5 text-stone-600 transition-colors hover:text-amber-300"
                    >
                      <Layers size={11} />
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Row 5 panels ───────────────────────────────────────────────────────────

function DataSufficiency({ rows }: { rows: DataRequirementRow[] }) {
  return (
    <div className="space-y-2.5">
      {rows.map((row) => {
        const pct = typeof row.completeness.value === 'number' ? row.completeness.value : null
        return (
          <div key={row.requirement} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[11px] text-stone-300">{row.label}</span>
              <span
                className={clsx(
                  'flex-shrink-0 font-mono text-[11px] tabular-nums',
                  figureToneClass(row.completeness.tone),
                )}
              >
                <FigureValue figure={row.completeness} align="right" />
              </span>
            </div>
            <div className="chart-viz-track h-1.5 w-full rounded-full">
              {pct != null && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{
                    width: `${Math.max(0, Math.min(100, pct))}%`,
                    background: chartSemanticProgressColor(pct),
                  }}
                />
              )}
            </div>
            <div className="text-[10px] text-stone-500">
              <FigureValue figure={row.outstanding} /> outstanding
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** The service ships plain aggregates here, not Figures — no scenario carries a
 *  drill descriptor, so nothing in this table is clickable. */
function SensitivityTable({ rows }: { rows: SensitivityRow[] }) {
  return (
    <table className="dense-table">
      <thead>
        <tr>
          <th className="text-left">Scenario</th>
          <th className="text-right">Runs</th>
          <th className="text-right">Δ DSCR</th>
          <th className="text-right">DSCR</th>
          <th className="text-right">LTV</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.scenario}>
            <td className="whitespace-nowrap text-stone-300">{row.label}</td>
            <td className="text-right font-mono tabular-nums text-stone-500">
              {formatFigure(fig('Runs', row.runs || null, 'count'))}
            </td>
            <td
              className={clsx(
                'text-right font-mono tabular-nums',
                row.deltaDscr == null
                  ? 'text-stone-600'
                  : row.deltaDscr < 0
                    ? 'text-red-400'
                    : 'text-emerald-400',
              )}
            >
              {/* fmtDscr treats a non-positive ratio as absent, and a negative
                  delta is a real measurement — so the signed delta is rendered
                  as text while the resulting levels use the ratio formatter. */}
              {formatFigure(fig('Δ DSCR', signedRatio(row.deltaDscr), 'text'))}
            </td>
            <td className="text-right font-mono tabular-nums">
              {formatFigure(fig('DSCR', row.resultingDscr, 'ratio'))}
            </td>
            <td className="text-right font-mono tabular-nums">
              {formatFigure(fig('LTV', row.resultingLtv, 'pct'))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function signedRatio(value: number | null): string | null {
  if (value == null) return null
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}x`
}

function ReviewQueue({ summary, rows }: { summary: Figure[]; rows: QuestionRow[] }) {
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="grid grid-cols-3 gap-1.5">
        {summary.map((tile) => (
          <div key={tile.label} className="metric-cell !px-2 !py-1.5">
            <div className="metric-cell-label truncate">{tile.label}</div>
            <div className={clsx('metric-cell-value !text-[14px]', figureToneClass(tile.tone))}>
              <FigureValue figure={tile} />
            </div>
          </div>
        ))}
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto scrollbar-thin">
        {rows.length === 0 ? (
          <p className="text-[11px] text-stone-500">No open questions on this queue.</p>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="surface-panel rounded-md px-2 py-1.5">
              <p className="line-clamp-2 text-[11px] leading-snug text-stone-200">{row.question}</p>
              <div className="mt-1 flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-stone-600">
                <span className="text-amber-200/70">{titleCase(row.directedTo)}</span>
                <span className="truncate">{row.askedBy ?? '--'}</span>
                <span className="ml-auto flex-shrink-0">{shortStamp(row.askedAt)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ── Inspector ──────────────────────────────────────────────────────────────

type InspectorTab = 'overview' | 'sources' | 'model' | 'risks' | 'audit'

const INSPECTOR_TABS: Array<{ key: InspectorTab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'sources', label: 'Sources' },
  { key: 'model', label: 'Model' },
  { key: 'risks', label: 'Risks' },
  { key: 'audit', label: 'Audit' },
]

/** Downside rows the reference calls for, mapped onto the scenarios the
 *  sensitivity service actually computes. */
const DOWNSIDE_ROWS: Array<{ scenario: string; label: string; read: 'dscr' | 'ltv' }> = [
  { scenario: 'noi_down_10', label: 'NOI −10% → DSCR', read: 'dscr' },
  { scenario: 'value_down_15', label: 'Value −15% → LTV', read: 'ltv' },
  { scenario: 'rate_up_100', label: 'Rates +100bp → DSCR', read: 'dscr' },
]

function InspectorTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="surface-panel rounded-md px-2 py-1.5">
      <div className="metric-cell-label truncate">{label}</div>
      <div className="mt-0.5 truncate text-[12px] text-stone-100">{value}</div>
    </div>
  )
}

function InspectorEmpty({ children }: { children: ReactNode }) {
  return <p className="text-[11px] leading-relaxed text-stone-500">{children}</p>
}

interface InspectorProps {
  row: QueueRow | null
  sensitivities: SensitivityRow[]
  attention: AttentionRow[]
  isLoading: boolean
  onClose: () => void
}

function Inspector({ row, sensitivities, attention, isLoading, onClose }: InspectorProps) {
  const [tab, setTab] = useState<InspectorTab>('overview')
  const navigate = useNavigate()
  const { open } = useDrillDown()

  // Attention is ranked across the whole queue; an item belongs to this deal
  // only when it points at the same workspace record.
  const deskRisks = attention.filter((item) => row?.href && item.href === row.href)
  const nextAction = deskRisks[0] ?? null

  const scenarioByKey = new Map(sensitivities.map((s) => [s.scenario, s]))

  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        <div className="h-4 w-2/3 rounded bg-white/[0.03]" />
        <div className="h-24 rounded bg-white/[0.03]" />
        <div className="h-32 rounded bg-white/[0.03]" />
      </div>
    )
  }

  if (!row) {
    return (
      <div className="p-3">
        <InspectorEmpty>
          Select an assignment in the queue to inspect its readiness, downside and open
          exceptions.
        </InspectorEmpty>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-col gap-2.5 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="terminal-kicker !text-[9px]">Underwriting inspector</div>
          <h2 className="truncate text-[13px] font-semibold text-stone-100" title={row.deal ?? ''}>
            {row.deal ?? row.loanId ?? '--'}
          </h2>
          <div className="font-mono text-[10px] text-stone-600">{row.loanId ?? '--'}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close inspector"
          className="-mr-1 -mt-1 flex-shrink-0 rounded p-1 text-stone-600 transition-colors hover:text-amber-300"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        {INSPECTOR_TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            className={clsx(
              'rounded border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] transition-colors',
              tab === entry.key
                ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200'
                : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="surface-panel rounded-md px-2.5 py-2">
            <div className="metric-cell-label">Decision snapshot</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-[22px] font-semibold leading-none text-stone-100">
                {formatFigure(fig('Diligence docs received', row.dataReadyPct, 'pct'))}
              </span>
              <span className="text-[10px] text-stone-500">diligence docs received</span>
            </div>
            <div className="chart-viz-track mt-1.5 h-1.5 w-full rounded-full">
              {row.dataReadyPct != null && (
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{
                    width: `${Math.max(0, Math.min(100, row.dataReadyPct))}%`,
                    background: chartSemanticProgressColor(row.dataReadyPct),
                  }}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            <InspectorTile label="Property" value={row.deal ?? '--'} />
            {/* The board payload carries no sponsor for an assignment. */}
            <InspectorTile label="Sponsor" value={<span className="text-stone-600">--</span>} />
            <InspectorTile label="Stage" value={titleCase(row.stage)} />
            <InspectorTile
              label="Requested"
              value={formatFigure(fig('Requested', row.amount, 'currencyM'))}
            />
          </div>

          <div>
            <div className="metric-cell-label mb-1">Base case</div>
            <InspectorEmpty>
              Value, debt yield, LTV, DSCR, occupancy and WALE are model outputs — the board
              endpoint returns readiness, exceptions and stage, not model results, so nothing is
              shown rather than a placeholder.
            </InspectorEmpty>
          </div>
        </>
      )}

      {tab === 'sources' && (
        <InspectorEmpty>
          The board payload carries no source-document inventory for an assignment. Data
          sufficiency by requirement is on the desk panel below the queue.
        </InspectorEmpty>
      )}

      {tab === 'model' && (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            <InspectorTile label="Model status" value={titleCase(row.modelStatus)} />
            <InspectorTile label="In stage" value={daysText(row.dwellDays)} />
          </div>
          <div>
            <div className="metric-cell-label mb-1">Downside</div>
            {sensitivities.length === 0 ? (
              <InspectorEmpty>No sensitivity runs on this queue.</InspectorEmpty>
            ) : (
              <div className="space-y-1">
                {DOWNSIDE_ROWS.map((entry) => {
                  const scenario = scenarioByKey.get(entry.scenario)
                  const value =
                    entry.read === 'dscr' ? scenario?.resultingDscr ?? null : scenario?.resultingLtv ?? null
                  return (
                    <div
                      key={entry.scenario}
                      className="flex items-baseline justify-between gap-2 border-b border-amber-400/[0.06] pb-1"
                    >
                      <span className="text-[11px] text-stone-400">{entry.label}</span>
                      <span className="font-mono text-[11px] tabular-nums text-stone-100">
                        {formatFigure(
                          fig(entry.label, value, entry.read === 'dscr' ? 'ratio' : 'pct'),
                        )}
                      </span>
                    </div>
                  )
                })}
                <p className="pt-1 text-[10px] leading-relaxed text-stone-500">
                  Averaged across every sensitivity run on this queue — not this deal alone.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'risks' && (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="metric-cell-label">Risks / exceptions</span>
            <span className="font-mono text-[10px] text-stone-500">
              {formatFigure(fig('Open exceptions', row.openExceptions, 'count'))} open
            </span>
          </div>
          {deskRisks.length === 0 ? (
            <InspectorEmpty>
              This assignment is not ranked on the desk attention list.
            </InspectorEmpty>
          ) : (
            deskRisks.map((item) => (
              <div key={item.title} className="surface-panel flex items-start gap-2 rounded-md px-2 py-1.5">
                <span
                  className={clsx(
                    'status-dot mt-1 flex-shrink-0',
                    SEVERITY_DOT[item.severity ?? ''] ?? 'bg-stone-600',
                  )}
                />
                <div className="min-w-0">
                  <div className="text-[11px] leading-snug text-stone-200">{item.title}</div>
                  {item.detail && <div className="text-[10px] text-stone-500">{item.detail}</div>}
                </div>
                <span className="ml-auto flex-shrink-0 font-mono text-[10px] text-stone-500">
                  {dueLabel(item.daysToDeadline)}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'audit' && (
        <InspectorEmpty>
          The board payload carries no per-assignment audit trail. Stage entry and due dates are
          on the queue row; record-level history opens in the workspace.
        </InspectorEmpty>
      )}

      <div className="mt-auto space-y-2 pt-2">
        <div>
          <div className="metric-cell-label">My next action</div>
          <p className="mt-0.5 text-[11px] leading-snug text-stone-200">
            {nextAction ? (
              <>
                {nextAction.action}
                {nextAction.detail ? ` — ${nextAction.detail}` : ''}
              </>
            ) : (
              <span className="text-stone-600">--</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => open(row.drill)}
            disabled={!row.drill}
            className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ClipboardList size={10} /> Open records
          </button>
          <button
            type="button"
            onClick={() => row.href && navigate(row.href)}
            disabled={!row.href}
            className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Layers size={10} /> Open in workspace
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

const TABS: DashboardTab[] = [
  { id: 'queue', label: 'Queue' },
  { id: 'desk', label: 'Desk & Team', icon: Users },
  { id: 'analysis', label: 'Diligence & Analysis' },
  { id: 'explorer', label: 'Pipeline Explorer', icon: Compass },
]

export default function UnderwritingManagerDashboard() {
  // The manager board opens on the whole desk: a manager (or the CEO) holding
  // one or two assignments of their own would otherwise read a two-deal desk.
  // "My queue" is one pick away and the choice lives in the URL.
  const [params, setParams] = useSearchParams()
  const picked = params.get('analyst') ?? DESK_SCOPE
  const analyst = picked === MY_QUEUE ? null : picked
  const setAnalyst = useCallback(
    (value: string | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('analyst', value ?? MY_QUEUE)
          return next
        },
        { replace: true },
      ),
    [setParams],
  )
  const board = useUnderwritingBoard(analyst)
  const { panels, isLoading, error } = board
  const retry = useCallback(() => void board.refetch(), [board])

  const scope = readPanel<ScopePanel>(panels, 'scope')
  const headline = readPanel<{ tiles: Figure[] }>(panels, 'headline')
  const stages = readPanel<{ rows: StageRow[] }>(panels, 'stages')
  const queue = readPanel<{ rows: QueueRow[]; total?: number }>(panels, 'queue')
  const universe = readPanel<BookUniverse>(panels, 'universe')
  const questions = readPanel<{ summary: Figure[]; rows: QuestionRow[] }>(panels, 'questions')
  const diligence = readPanel<{
    statuses: Array<{ key: string; label: string }>
    rows: Array<{ itemType: string; label: string; cells: Figure[] }>
  }>(panels, 'diligence')
  const dataRequirements = readPanel<{ rows: DataRequirementRow[] }>(panels, 'dataRequirements')
  const sensitivities = readPanel<{ rows: SensitivityRow[] }>(panels, 'sensitivities')
  const attention = readPanel<{ rows: AttentionRow[] }>(panels, 'attention')
  const roster = readPanel<{ rows: RosterRow[]; basis?: string }>(panels, 'roster')
  const pipelineFunnel = readPanel<FunnelResponse>(panels, 'pipelineFunnel')
  const monthlyFlow = readPanel<FlowResponse>(panels, 'monthlyFlow')
  const leaderboards = readPanel<LeaderboardsPanel>(panels, 'leaderboards')
  const team = readPanel<{ people: Array<{ name: string }>; basis?: string }>(panels, 'team')
  const workload = readPanel<WorkloadResponse>(panels, 'workload')

  const [view, setView] = useDashboardView(TABS)
  const [tab, setTab] = useState<QueueTab>('all')
  const [search, setSearch] = useState('')
  const [expressionDraft, setExpressionDraft] = useState('')
  const [expression, setExpression] = useState('')
  const [showLegend, setShowLegend] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [compact, setCompact] = useState(true)
  // The analyst column shows: the board opens on the whole desk, where who holds a deal matters.
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])
  const [inspectorOpen, setInspectorOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const allColumns = useMemo(buildQueueColumns, [])
  const visibleColumns = useMemo(
    () => allColumns.filter((column) => !hiddenColumns.includes(column.key)),
    [allColumns, hiddenColumns],
  )

  const queueRows = useMemo(() => queue?.rows ?? [], [queue])
  const filteredRows = useMemo(() => {
    const tabMatch = QUEUE_TABS.find((entry) => entry.key === tab)?.match ?? (() => true)
    const needle = search.trim().toLowerCase()
    const tokens = expression.trim().split(/\s+/).filter(Boolean)
    return queueRows.filter((row) => {
      if (!tabMatch(row)) return false
      if (needle && !TEXT_FIELDS.deal(row).toLowerCase().includes(needle)) return false
      return tokens.every((token) => matchesToken(row, token))
    })
  }, [queueRows, tab, search, expression])

  const selected =
    filteredRows.find((row) => row.id === selectedId) ?? filteredRows[0] ?? null

  // The reference highlights the stage the operator is working in; that is the
  // stage of the assignment under inspection, not a stored flag.
  const activeStage = selected?.stage ?? null

  const diligenceMatrix: MatrixPanelData | null = diligence
    ? {
        columns: diligence.statuses.map((status) => ({ key: status.key, label: status.label })),
        rows: diligence.rows.map((row) => ({
          key: row.itemType,
          label: row.label,
          cells: row.cells,
        })),
      }
    : null

  // The only export descriptor the page can trust is one the server shipped —
  // a client-assembled filter set would 400 against the dataset schema.
  const exportDescriptor = headline?.tiles.find((tile) => tile.drill)?.drill ?? null

  const exportQueue = async () => {
    if (!exportDescriptor) return
    setExportBusy(true)
    setExportError(null)
    try {
      const outcome = await dashboardsService.drillExport({
        dataset: exportDescriptor.dataset,
        filters: exportDescriptor.filters,
      })
      downloadDrillExport(outcome.blob, outcome.filename)
    } catch (err) {
      setExportError(panelErrorMessage(err))
    } finally {
      setExportBusy(false)
    }
  }

  const toolbar = (
    <>
      <label className="flex items-center gap-1 text-[10px] text-stone-500">
        <Users size={11} className="text-stone-600" />
        <select
          value={analyst ?? ''}
          onChange={(event) => {
            setAnalyst(event.target.value || null)
            setSelectedId(null)
          }}
          className="rounded border border-amber-400/15 bg-[#090909] px-1.5 py-0.5 text-[10px] text-stone-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
          aria-label="Queue scope"
          title={roster?.basis ?? 'Every analyst with an assignment, every person named on a live deal, and the team roster'}
        >
          <option value={DESK_SCOPE}>Whole desk</option>
          <option value="">My queue</option>
          {(roster?.rows ?? []).map((row) => (
            <option
              key={row.scopeValue ?? row.analystEmail ?? 'unassigned'}
              value={row.scopeValue ?? row.analystEmail ?? ''}
            >
              {rosterOptionLabel(row)}
            </option>
          ))}
        </select>
      </label>

      {analyst && analyst !== DESK_SCOPE && !analyst.startsWith('__') && (
        <Link
          to={`/dashboards/people?view=person&role=underwriter&name=${encodeURIComponent(analyst)}`}
          className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
        >
          <UserRound size={10} /> Profile
        </Link>
      )}
      <Link
        to="/dashboards/people"
        className="flex items-center gap-1 rounded border border-amber-400/15 px-2 py-1 text-[10px] text-stone-300 transition-colors hover:border-amber-400/35 hover:text-amber-200"
      >
        <Users size={10} /> People &amp; Team
      </Link>

      <button
        type="button"
        onClick={() => void exportQueue()}
        disabled={!exportDescriptor || exportBusy}
        title={exportError ?? 'Export this queue to Excel'}
        className={clsx(
          'flex items-center gap-1 rounded border px-2 py-1 text-[10px] transition-colors',
          exportError
            ? 'border-red-500/30 text-red-300'
            : 'border-amber-400/25 text-amber-200 hover:bg-amber-400/10',
          'disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent',
        )}
      >
        <FileSpreadsheet size={10} />
        {exportBusy ? 'Exporting…' : exportError ? 'Export failed' : 'Excel'}
      </button>

      <button
        type="button"
        onClick={() => setInspectorOpen((value) => !value)}
        aria-pressed={inspectorOpen}
        className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
      >
        {inspectorOpen ? <PanelRightClose size={10} /> : <PanelRightOpen size={10} />}
        Inspector
      </button>
    </>
  )

  const scopeName = scope ? scopeLabel(scope, picked) : 'Queue in view'

  const renderActiveGrid = () =>
    view === 'explorer' ? (
      <PipelineExplorer />
    ) : view === 'desk' ? (
      <DashboardGrid rows={4}>
        <GridCell panelId="uw-funnel" panelLabel="Pipeline funnel" col={2} row={2}>
          <Panel
            title="Pipeline funnel"
            kicker={scopeName}
            isLoading={isLoading}
            isEmpty={!isLoading && !pipelineFunnel}
            emptyIcon={GitBranch}
            emptyTitle="Funnel unavailable"
            emptyDescription="The board response carries no pipeline funnel."
            className="flex-1"
            bodyClassName="!py-2"
            scroll
            footer={
              <BasisNote full={pipelineFunnel?.basis}>
                Deals in each stage now; % = share that reached the next stage; dwell in 24-hour days.
              </BasisNote>
            }
          >
            <FunnelStrip data={pipelineFunnel} orientation="column" scopeLabel={scopeName} />
          </Panel>
        </GridCell>
        <GridCell panelId="uw-flow" panelLabel="Monthly flow" col={2} row={2}>
          <Panel
            title="Monthly flow · 24 months"
            kicker={scopeName}
            isLoading={isLoading}
            isEmpty={!isLoading && !monthlyFlow}
            emptyIcon={LineChartIcon}
            emptyTitle="Flow unavailable"
            emptyDescription="The board response carries no monthly flow."
            className="flex-1"
            bodyClassName="flex min-h-0 flex-col !pb-1"
          >
            <FlowChart data={monthlyFlow} scopeLabel={scopeName} />
          </Panel>
        </GridCell>
        <GridCell panelId="uw-workload" panelLabel="Team workload" col={2} row={2}>
          <TeamWorkloadPanel
            served={workload}
            servedLoading={isLoading}
            compact
            title={`Desk workload · ${team?.people.length ?? workload?.people.length ?? 0} people`}
            className="flex-1"
          />
        </GridCell>
        <GridCell panelId="uw-leaderboard" panelLabel="Underwriter leaderboard" col={2} row={2}>
          <Leaderboard
            role="underwriter"
            served={leaderboards?.underwriter?.rows ?? null}
            title="Underwriter leaderboard"
            className="flex-1"
          />
        </GridCell>
      </DashboardGrid>
    ) : view === 'queue' ? (
      <DashboardGrid rows={4}>
        <GridCell panelId="underwriting-flow" panelLabel="Underwriting flow" col={4} row={1}>
          <Panel
            title="Underwriting flow"
            kicker="Live assignments by stage · average days in stage"
            isLoading={isLoading}
            isEmpty={!isLoading && !error && (stages?.rows.length ?? 0) === 0}
            emptyIcon={Gauge}
            emptyTitle="No staged assignments"
            emptyDescription="No live assignment sits in any underwriting stage for this queue."
            skeletonRows={3}
            className="flex-1"
            bodyClassName="!p-2"
          >
            {stages && <StageFlow rows={stages.rows} activeStage={activeStage} />}
          </Panel>
        </GridCell>

        <GridCell panelId="underwriting-queue" panelLabel="My underwriting queue" col={4} row={3}>
          <Panel
            title="My underwriting queue"
            kicker={queueKicker(filteredRows.length, queueRows.length, queue?.total)}
            isLoading={isLoading}
            isEmpty={!isLoading && !error && queueRows.length === 0}
            emptyIcon={Inbox}
            emptyTitle="No live assignments"
            emptyDescription="This queue holds no assignment that is not already complete."
            className="flex-1"
            bodyClassName="!p-0 flex flex-col"
            scroll
            actions={
              <div className="flex flex-wrap gap-1">
                {QUEUE_TABS.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => setTab(entry.key)}
                    aria-pressed={tab === entry.key}
                    className={clsx(
                      'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
                      tab === entry.key
                        ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200'
                        : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
                    )}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            }
          >
            {filteredRows.length === 0 && queueRows.length > 0 ? (
              <p className="p-3 text-[11px] text-stone-500">
                No assignment on this queue matches the current tab, search and filter expression.
              </p>
            ) : (
              <QueueTable
                rows={filteredRows}
                columns={visibleColumns}
                selectedId={selected?.id ?? null}
                onSelect={(row) => {
                  setSelectedId(row.id)
                  setInspectorOpen(true)
                }}
                compact={compact}
              />
            )}
          </Panel>
        </GridCell>
      </DashboardGrid>
    ) : (
      <DashboardGrid rows={4}>
        <GridCell panelId="data-sufficiency" panelLabel="Data sufficiency" col={2} row={2}>
          <Panel
            title="Data sufficiency"
            isLoading={isLoading}
            isEmpty={!isLoading && !error && (dataRequirements?.rows.length ?? 0) === 0}
            emptyIcon={ClipboardList}
            emptyTitle="No data requirements"
            emptyDescription="No requirement is tracked against the deals on this queue."
            skeletonRows={5}
            className="flex-1"
            scroll
          >
            {dataRequirements && <DataSufficiency rows={dataRequirements.rows} />}
          </Panel>
        </GridCell>

        <GridCell panelId="sensitivity" panelLabel="Sensitivity" col={2} row={2}>
          <Panel
            title="Sensitivity"
            kicker="Desk-wide"
            isLoading={isLoading}
            isEmpty={!isLoading && !error && (sensitivities?.rows.length ?? 0) === 0}
            emptyIcon={Gauge}
            emptyTitle="No sensitivity runs"
            emptyDescription="No scenario has been computed for the assignments on this queue."
            skeletonRows={5}
            className="flex-1"
            bodyClassName="!p-0"
            scroll
            footer="Averages across every run on the queue. No scenario ships a drill descriptor, so these are read-only."
          >
            {sensitivities && <SensitivityTable rows={sensitivities.rows} />}
          </Panel>
        </GridCell>

        <GridCell panelId="diligence" panelLabel="Diligence" col={2} row={2}>
          <Panel
            title="Diligence"
            isLoading={isLoading}
            isEmpty={!isLoading && !error && (diligenceMatrix?.rows.length ?? 0) === 0}
            emptyIcon={ListChecks}
            emptyTitle="No diligence items"
            emptyDescription="No third-party item is open against the deals on this queue."
            skeletonRows={5}
            className="flex-1"
            bodyClassName="!p-0"
            scroll
          >
            {diligenceMatrix && <MatrixPanel data={diligenceMatrix} />}
          </Panel>
        </GridCell>

        <GridCell panelId="review-queue" panelLabel="Review queue" col={2} row={2}>
          <Panel
            title="Review queue"
            kicker="Open questions"
            isLoading={isLoading}
            isEmpty={
              !isLoading &&
              !error &&
              (questions?.summary.length ?? 0) === 0 &&
              (questions?.rows.length ?? 0) === 0
            }
            emptyIcon={MessageSquare}
            emptyTitle="No open questions"
            emptyDescription="Nothing is outstanding with a reviewer, borrower or originator."
            skeletonRows={5}
            className="flex-1"
            scroll
          >
            {questions && <ReviewQueue summary={questions.summary} rows={questions.rows} />}
          </Panel>
        </GridCell>
      </DashboardGrid>
    )

  return (
    <DashboardShell
      dashboardId="underwriting"
      dashboardView={view}
      kicker="Pre-closing"
      title="Underwriting Workspace"
      subtitle={
        scope
          ? `${scopeLabel(scope, picked)} · ${scope.assignments} live assignment${scope.assignments === 1 ? '' : 's'}${scope.unassigned ? ' with no analyst on record' : ''}`
          : 'My assignments, diligence status and model readiness across the desk.'
      }
      partition={board.partition}
      dataUpdatedAt={board.dataUpdatedAt}
      isFetching={board.isFetching}
      error={error}
      onRetry={retry}
      filters={toolbar}
      tabs={<DashboardTabs tabs={TABS} active={view} onChange={setView} />}
      fill
    >
      <div className="flex h-full min-h-0 gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {view !== 'explorer' && (
            <>
              <KpiStrip
                tiles={headline?.tiles ?? []}
                columnsClassName="grid-cols-4 xl:grid-cols-8"
                isLoading={isLoading}
                className="flex-shrink-0"
              />
              <BookUniverseLine universe={universe} className="flex-shrink-0 px-1" />
            </>
          )}

          {view === 'queue' && (
          <div className="surface-panel flex flex-shrink-0 flex-wrap items-center gap-1.5 rounded-md px-2 py-1.5">
            <div className="flex min-w-[180px] flex-1 items-center gap-1.5 rounded border border-amber-400/12 bg-[#090909] px-2 py-1">
              <Search size={11} className="flex-shrink-0 text-stone-600" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search deal or loan id"
                aria-label="Search the queue"
                className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none"
              />
            </div>

            <form
              className="flex min-w-[220px] flex-[1.4] items-center gap-1.5 rounded border border-amber-400/12 bg-[#090909] px-2 py-1"
              onSubmit={(event) => {
                event.preventDefault()
                setExpression(expressionDraft)
              }}
            >
              <SlidersHorizontal size={11} className="flex-shrink-0 text-stone-600" />
              <input
                value={expressionDraft}
                onChange={(event) => setExpressionDraft(event.target.value)}
                placeholder="stage:qc_review due:<=2 exceptions:>0"
                aria-label="Filter expression"
                className="min-w-0 flex-1 bg-transparent font-mono text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none"
              />
            </form>

            <button
              type="button"
              onClick={() => setExpression(expressionDraft)}
              className="rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setShowLegend((value) => !value)}
              aria-expanded={showLegend}
              className="flex items-center gap-1 rounded border border-amber-400/15 px-2 py-1 text-[10px] text-stone-400 transition-colors hover:text-stone-200"
            >
              <Filter size={10} /> Filters
            </button>
            <button
              type="button"
              onClick={() => setShowColumns((value) => !value)}
              aria-expanded={showColumns}
              className="flex items-center gap-1 rounded border border-amber-400/15 px-2 py-1 text-[10px] text-stone-400 transition-colors hover:text-stone-200"
            >
              <Columns3 size={10} /> Columns
            </button>

            <div className="flex items-center gap-0.5 rounded border border-amber-400/15 p-0.5">
              <button
                type="button"
                onClick={() => setCompact(true)}
                aria-pressed={compact}
                title="Compact rows"
                className={clsx(
                  'rounded p-1 transition-colors',
                  compact ? 'bg-amber-400/10 text-amber-200' : 'text-stone-600 hover:text-stone-300',
                )}
              >
                <Rows3 size={10} />
              </button>
              <button
                type="button"
                onClick={() => setCompact(false)}
                aria-pressed={!compact}
                title="Roomy rows"
                className={clsx(
                  'rounded p-1 transition-colors',
                  !compact ? 'bg-amber-400/10 text-amber-200' : 'text-stone-600 hover:text-stone-300',
                )}
              >
                <ListChecks size={10} />
              </button>
            </div>

            {showLegend && (
              <div className="w-full border-t border-amber-400/[0.06] pt-1.5">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {EXPRESSION_TOKENS.map((entry) => (
                    <span key={entry.token} className="text-[10px] text-stone-500">
                      <code className="font-mono text-amber-200/70">{entry.token}</code> —{' '}
                      {entry.describes}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {showColumns && (
              <div className="flex w-full flex-wrap gap-x-4 gap-y-1 border-t border-amber-400/[0.06] pt-1.5">
                {allColumns.map((column) => (
                  <label
                    key={column.key}
                    className={clsx(
                      'flex items-center gap-1 text-[10px]',
                      column.optional ? 'text-stone-400' : 'text-stone-600',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={!hiddenColumns.includes(column.key)}
                      disabled={!column.optional}
                      onChange={() =>
                        setHiddenColumns((current) =>
                          current.includes(column.key)
                            ? current.filter((key) => key !== column.key)
                            : [...current, column.key],
                        )
                      }
                      className="h-3 w-3 accent-amber-400"
                    />
                    {column.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          )}

          {renderActiveGrid()}
        </div>

        {inspectorOpen && selected && (
          <aside className="flex w-[360px] flex-shrink-0 flex-col overflow-y-auto scrollbar-thin border-l border-amber-400/[0.08]">
            <Inspector
              row={selected}
              sensitivities={sensitivities?.rows ?? []}
              attention={attention?.rows ?? []}
              isLoading={isLoading}
              onClose={() => setInspectorOpen(false)}
            />
          </aside>
        )}
      </div>
    </DashboardShell>
  )
}
